import {
  DialogContent,
  DialogClose,
  DialogTitle,
  Dialog,
} from "@/components/ui/dialog";
import IconCross from '@/assets/icons/cross.svg';
import IconPhantom from '@/assets/icons/phantom.svg';
import IconSolflare from '@/assets/icons/solflare.jpeg';
import IconBackpack from '@/assets/icons/backpack.png';
import IconMagicEden from '@/assets/icons/magiceden.png';
import IconMWA from '@/assets/icons/solana.svg';
import { VisuallyHidden } from '@radix-ui/react-visually-hidden';
import { trackEvent } from '@/utils/analytics';
import { EventAction } from '@/types/analytics';
import { shortenAddress } from '@/lib/utils';
import type { WalletAdapterName } from '@/contexts/WalletContext';
import IconWalletCross from '@/assets/icons/wallet_cross.svg';
import IconArrowDownPurple from '@/assets/icons/arrow_down_purple.svg';
import useModalStore from "@/stores/useModalStore";

const getWalletIcon = (walletType?: WalletAdapterName): string => {
  switch (walletType?.toLowerCase()) {
    case 'phantom':
      return IconPhantom;
    case 'solflare':
      return IconSolflare;
    case 'backpack':
      return IconBackpack;
    case 'magiceden':
      return IconMagicEden;
    case 'mwa':
      return IconMWA;
    default:
      return IconPhantom; // Default fallback
  }
};

export default function WalletMismatchModal() {
  const walletMissMatch = useModalStore((state) => state.walletMissMatch);
  const setWalletMismatchModal = useModalStore((state) => state.setWalletMismatchModal);

  const handleModalClose = (reason: 'cross' | 'backdrop') => {
    trackEvent('ui_interaction', EventAction.MODAL_CLOSE, {
      label: `wallet_mismatch_modal_${reason}`,
      component: 'wallet_mismatch_modal',
    });
    onCloseDialog();
  };

  const onCloseDialog = () => {
    setWalletMismatchModal({
      show: false,
      primaryWallet: undefined,
      connectedWallet: undefined,
    });
  };

  return (
    <Dialog open={walletMissMatch.show} onOpenChange={onCloseDialog}>
      <DialogContent
        className="w-[95%] max-w-[36.563rem] mx-auto bg-[#171921] border border-gray-800 text-white px-7 py-9 rounded-2xl"
        shouldShowCloseButton={false}
        onInteractOutside={() => handleModalClose('backdrop')}
      >
        {/* Dummy Title to fix the error of DialogContent not having a title */}
        <VisuallyHidden>
          <DialogTitle></DialogTitle>
        </VisuallyHidden>

        <div className="flex flex-row items-center justify-end mb-6">
          <DialogClose asChild>
            <button
              className="outline-none"
              onClick={() => handleModalClose('cross')}
            >
              <img
                src={IconCross}
                alt="cross"
                className="w-[1.88rem] h-[1.88rem]"
              />
            </button>
          </DialogClose>
        </div>

        {/* Content */}
        <div className="flex flex-col">
          <div className="flex flex-col items-center mb-6">
            <img
              src={IconWalletCross}
              alt="wallet cross"
              className="w-[6.75rem] h-[6.75rem] mb-8"
            />
            <p className="font-inter font-medium text-xl text-center text-white">
              Your wallets are mismatched.
            </p>
            <p className="font-inter font-medium text-xl text-center text-white">
              Switch manually to continue
            </p>
          </div>
          <div className="bg-[#0D0E13] p-3 rounded-xl mb-6">
            <div className="border border-[#303030] rounded-xl py-7 text-center">
              <p className="text-base font-inter text-[#CACACA] mb-3">
                Currently active in wallet
              </p>
              <div className="flex flex-row items-center justify-center">
                <img
                  src={getWalletIcon(walletMissMatch.connectedWallet?.adapter)}
                  alt={walletMissMatch.connectedWallet?.adapter || 'wallet'}
                  className="w-6 h-6 rounded-full object-cover mr-3"
                />
                <span className="text-white font-inter text-base">
                  {shortenAddress(walletMissMatch.connectedWallet?.address ?? '')}
                </span>
              </div>

              <div className="flex justify-center items-center my-5">
                <div className="w-full h-[1px] bg-[#303030]"></div>
                <img
                  src={IconArrowDownPurple}
                  alt="arrow down purple"
                  className="w-[19px] h-[19px] absolute object-contain"
                />
                <div className="w-full h-[1px] bg-[#303030]"></div>
              </div>

              <p className="text-base font-inter text-[#CACACA] mb-3">
                Switch to this wallet
              </p>
              {walletMissMatch.primaryWallet?.walletAdapter && (
                <div className="flex flex-row items-center justify-center">
                  <img
                    src={getWalletIcon(walletMissMatch.primaryWallet.walletAdapter)}
                    alt={walletMissMatch.primaryWallet?.walletAdapter || 'wallet'}
                    className="w-6 h-6 rounded-full object-cover mr-3"
                  />
                  <span className="text-white font-inter text-base">
                    {shortenAddress(walletMissMatch.primaryWallet?.publicKey ?? '')}
                  </span>
                </div>
              )}
            </div>
          </div>

          <button
            className="w-full py-3 px-4 bg-[#873ED0] hover:bg-[#9B4FE0] transition-colors rounded-lg font-inter font-semibold text-white"
            onClick={() => handleModalClose('cross')}
          >
            Ok, I am Understand
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
